import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import EventLayout from "@/components/event/EventLayout";
import { Button } from "@/components/ui/button";
import { humanDay } from "@/lib/eventHub";
import { CheckCircle2, Calendar, Camera, Heart, Sparkles, X } from "lucide-react";

const REDIRECT_SECONDS = 8;

export default function CheckInSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [paused, setPaused] = useState(false);

  const attendee = state.attendee;
  const eventDay = state.event_day || "";
  const checkedInAt = state.checked_in_at || "";
  const alreadyCheckedIn = state.already_checked_in;
  const walkIn = state.walk_in;

  useEffect(() => {
    if (!attendee) {
      navigate("/event/checkin", { replace: true });
      return;
    }
    if (paused) return;
    if (countdown <= 0) {
      navigate("/event/program");
      return;
    }
    const t = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, paused, attendee, navigate]);

  if (!attendee) return null;

  const eventName = eventDay === "both"
    ? "VIP Networking Experience + Business Showers"
    : eventDay === "2026-07-17"
      ? "VIP Networking Experience"
      : "Freedom In Me Business Showers";

  return (
    <EventLayout showProgramBanner={false}>
      <div className="mx-auto max-w-lg px-5 py-10 min-h-[70vh] flex flex-col items-center text-center">
        <div className={`relative h-24 w-24 rounded-full flex items-center justify-center mb-4 shadow-2xl ${alreadyCheckedIn ? "bg-emerald-100" : "bg-fuchsia-100"}`}>
          {alreadyCheckedIn ? (
            <span className="text-emerald-700 text-5xl">👋</span>
          ) : (
            <CheckCircle2 className="text-fuchsia-700" size={64} strokeWidth={2} />
          )}
          {!alreadyCheckedIn && (
            <>
              <span className="absolute -top-1 -right-1"><Sparkles size={18} className="text-fuchsia-500" /></span>
              <span className="absolute -bottom-1 -left-1"><Sparkles size={14} className="text-fuchsia-400" /></span>
            </>
          )}
        </div>

        <h1 className="font-[Cormorant_Garamond] text-4xl sm:text-5xl leading-tight text-emerald-950 mb-2">
          {alreadyCheckedIn ? "Welcome Back!" : "You're Checked In!"}
        </h1>
        {alreadyCheckedIn ? (
          <p className="font-[Outfit] text-sm text-emerald-950/70 mb-6 max-w-xs">
            You’re already checked in for today. Enjoy the experience!
          </p>
        ) : (
          <p className="font-[Outfit] text-sm text-emerald-950/70 mb-6 max-w-xs">
            Welcome to the 2026 Freedom In Me Business Showers.
          </p>
        )}

        <div data-testid="checkin-success-card" className="w-full rounded-2xl border-2 border-emerald-950 bg-emerald-950 text-white p-5 mb-6 text-left shadow-xl">
          <div className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-300 mb-2">Attendee</div>
          <div className="font-[Cormorant_Garamond] text-3xl leading-tight">{attendee.first_name} {attendee.last_name}</div>
          <div className="mt-3 border-t border-emerald-800 pt-3">
            <div className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-300 mb-1">Event</div>
            <div className="font-[Outfit] font-semibold text-base">{eventName}</div>
            <div className="text-xs text-emerald-100/70 mt-1 font-[Outfit]">
              {humanDay(eventDay)}
              {checkedInAt && (
                <> · Checked in at {new Date(checkedInAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET</>
              )}
            </div>
            {walkIn && (
              <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 px-2.5 py-0.5">
                <span className="text-[10px] font-[Outfit] font-bold tracking-wider uppercase text-fuchsia-100">Walk-In</span>
              </div>
            )}
          </div>
        </div>

        <div className="w-full space-y-3 mb-4">
          <Button
            asChild
            data-testid="success-open-program"
            className="w-full h-14 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-[Outfit] font-bold text-base shadow-lg shadow-fuchsia-600/20"
          >
            <Link to="/event/program">
              <Calendar size={18} className="mr-2" />
              Open Today’s Program
            </Link>
          </Button>
          <div className="grid grid-cols-2 gap-3">
            <Button
              asChild
              data-testid="success-share-photos"
              variant="outline"
              className="h-12 rounded-xl border-2 border-emerald-950 text-emerald-950 bg-white hover:bg-emerald-950 hover:text-white font-[Outfit] font-bold text-sm"
            >
              <Link to="/event/photos"><Camera size={16} className="mr-1.5" /> Photos</Link>
            </Button>
            <Button
              asChild
              data-testid="success-donate"
              variant="outline"
              className="h-12 rounded-xl border-2 border-fuchsia-600 text-fuchsia-700 bg-white hover:bg-fuchsia-600 hover:text-white font-[Outfit] font-bold text-sm"
            >
              <Link to="/event#donation" onClick={() => setPaused(true)}><Heart size={16} className="mr-1.5" /> Donate</Link>
            </Button>
          </div>
        </div>

        {!paused && countdown > 0 && (
          <div data-testid="success-auto-redirect" className="flex items-center gap-2 text-xs font-[Outfit] text-emerald-950/50 mt-2">
            <span>Opening program in {countdown}s…</span>
            <button
              onClick={() => setPaused(true)}
              data-testid="success-cancel-redirect"
              className="inline-flex items-center gap-1 font-semibold text-emerald-950/70 hover:text-fuchsia-700 underline underline-offset-2"
            >
              <X size={12} /> Stay here
            </button>
          </div>
        )}

        <Link
          to="/event/checkin"
          data-testid="success-another-guest"
          className="mt-6 text-xs font-[Outfit] text-emerald-950/50 hover:text-fuchsia-700 underline underline-offset-2"
          onClick={() => setPaused(true)}
        >
          Not you? Check in another guest
        </Link>
      </div>
    </EventLayout>
  );
}
